import React, { useRef } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeCanvas } from 'qrcode.react';

const CHARGE_DOMAIN = 'https://charge.greenwiser.app';

const QR_SIZE = 1024;
const PADDING = 64;
const CODE_TEXT_HEIGHT = 180;
const SCAN_HINT_LINE_HEIGHT = 52;
const SCAN_HINT_BLOCK_HEIGHT = SCAN_HINT_LINE_HEIGHT * 2 + 30;
const TOTAL_HEIGHT = QR_SIZE + PADDING * 2 + CODE_TEXT_HEIGHT + SCAN_HINT_BLOCK_HEIGHT;
const TOTAL_WIDTH = QR_SIZE + PADDING * 2;
const HINT_EN = 'Scan with your phone, or enter the code in the Green Wiser app';
const HINT_ZH = '請用手機掃描,或於 Green Wiser 應用程式內輸入此代碼';

interface PrintableQRProps {
    qrCode: string;
    connectorId?: number;
    chargerName: string;
    siteName?: string;
}

export const PrintableQR: React.FC<PrintableQRProps> = ({
    qrCode,
    connectorId,
    chargerName,
    siteName,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const codeWithConnector = connectorId ? `${qrCode}-${connectorId}` : qrCode;
    const url = `${CHARGE_DOMAIN}/c/${codeWithConnector}`;
    const label = connectorId
        ? `${chargerName} — Connector ${connectorId}`
        : chargerName;
    const filename = `qr-${codeWithConnector}.png`;

    const handleDownload = () => {
        const qrCanvas = canvasRef.current;
        if (!qrCanvas) return;

        // Composite the QR + the human-readable code on a larger canvas so
        // the downloaded PNG is self-contained: if a printed sticker is
        // damaged or the camera can't focus, the user can read the code
        // and type it manually in the app.
        const out = document.createElement('canvas');
        out.width = TOTAL_WIDTH;
        out.height = TOTAL_HEIGHT;
        const ctx = out.getContext('2d');
        if (!ctx) return;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);

        ctx.drawImage(qrCanvas, PADDING, PADDING, QR_SIZE, QR_SIZE);

        const codeY = PADDING + QR_SIZE + 40;
        ctx.fillStyle = '#1a1a1a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = 'bold 140px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
        ctx.fillText(codeWithConnector, out.width / 2, codeY);

        // Two-line hint: English + Traditional Chinese, so the printed
        // sticker is usable for HK / TW / overseas users alike. Auto-shrink
        // the font if either line would overflow the canvas width — keeps
        // the layout safe if the hint text is edited longer in the future.
        const hintEnY = codeY + CODE_TEXT_HEIGHT - 10;
        const hintZhY = hintEnY + SCAN_HINT_LINE_HEIGHT;
        const hintFontStack = '-apple-system, "Segoe UI", Roboto, "PingFang TC", "PingFang SC", "Microsoft JhengHei", "Microsoft YaHei", sans-serif';
        const maxHintWidth = out.width - PADDING * 2;
        let hintFontSize = 36;
        ctx.font = `${hintFontSize}px ${hintFontStack}`;
        const widest = Math.max(ctx.measureText(HINT_EN).width, ctx.measureText(HINT_ZH).width);
        if (widest > maxHintWidth) {
            hintFontSize = Math.floor(hintFontSize * (maxHintWidth / widest));
            ctx.font = `${hintFontSize}px ${hintFontStack}`;
        }
        ctx.fillStyle = '#666666';
        ctx.fillText(HINT_EN, out.width / 2, hintEnY);
        ctx.fillText(HINT_ZH, out.width / 2, hintZhY);

        const link = document.createElement('a');
        link.href = out.toDataURL('image/png');
        link.download = filename;
        link.click();
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                textAlign: 'center',
                width: 280,
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {label}
            </Typography>
            {siteName && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 2 }}
                >
                    {siteName}
                </Typography>
            )}
            <Box
                sx={{
                    p: 2,
                    bgcolor: '#fff',
                    border: '1px solid #F5F5F5',
                    borderRadius: 1,
                    display: 'inline-block',
                    mb: 1,
                }}
            >
                <QRCodeCanvas
                    ref={canvasRef}
                    value={url}
                    size={1024}
                    level="M"
                    includeMargin
                    style={{ width: 200, height: 200 }}
                />
            </Box>
            <Typography
                variant="h6"
                sx={{
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    mb: 0.5,
                }}
            >
                {codeWithConnector}
            </Typography>
            <Typography
                variant="caption"
                display="block"
                sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    mb: 2,
                    wordBreak: 'break-all',
                    fontSize: '0.7rem',
                }}
            >
                {url}
            </Typography>
            <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                onClick={handleDownload}
                sx={{ alignSelf: 'stretch' }}
            >
                Download PNG
            </Button>
        </Paper>
    );
};
